import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import SliderStrip, { type SliderStripProps } from 'Components/slider/sliderStrip';
import ColorTile from 'Components/smallComponents/colorTile';
import type { TemplateResult } from 'Components/types';
import { replaceColor } from 'Core/actions/request';
import { createSelectedColorSelector } from 'Core/selectors/colorSearch';
import { useDispatch, useSelector } from 'Core/store';
import type { WeightedColor } from 'Models/index';
import isLight from 'Modules/colorLightness';

import facetsPanelConfig from './facetsPanelConfig';

import './colorSlider.scss';

interface Props {
  color: string;
  field: string;
}

export default function ColorSlider({ color: color_pr, field }: Props): TemplateResult {
  const dispatch = useDispatch();

  const colorSelector = useMemo(() => createSelectedColorSelector(color_pr), [color_pr]);
  const color = useSelector(colorSelector, { equalityFn: colorComparer });

  const activeRef = useRef(false);
  const [selected, setSelected] = useState(color);
  const selectedRef = useRef(selected);

  const { searchOptions } = useContext(facetsPanelConfig);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  if (!color || !selected) {
    return null;
  }

  if (!activeRef.current && color.weight !== selected.weight) {
    setSelected(color);
  }

  const onChangeStarted = () => {
    activeRef.current = true;
  };
  const onChange = (nextSelected: number[]) => setSelected(selected.clone({ weight: nextSelected[1] }));
  const onChangeFinished = () => {
    activeRef.current = false;
    const item = selectedRef.current;
    if (!item) {
      return;
    }
    dispatch(replaceColor({ field, term: item.term }, { ...searchOptions, mayDiscardValue: true }));
  };

  const sliderProps = {
    field: color.color,
    selected: [0, selected.weight],
    mode: 'UpperBound',
    color: selected.colorStyle,
    onChange,
    onChangeStarted,
    onChangeFinished,
  } satisfies SliderStripProps;
  const slider = <SliderStrip {...sliderProps} key={`sliderstrip-for-${field}`} />;

  const discardColor = () => {
    dispatch(
      replaceColor(
        { field, term: selected.term, isSelected: false },
        { ...searchOptions, mayDiscardValue: true },
      ),
    );
  };

  return (
    <div className={`cm_color-slider${isLight(color.color) ? ' light' : ''}`}>
      <div className="discard" onClick={discardColor} />
      <div className="strip">{slider}</div>
      <ColorTile color={color.color} />
    </div>
  );
}

function colorComparer(prev: WeightedColor | undefined, curr: WeightedColor | undefined) {
  return prev === curr || (!!prev && !!curr && prev.equals(curr));
}
